import React from 'react'
import SubscriptionPlanPreview from 'src/components/subscriptions/preview/SubscriptionPlanPreview'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import usePaymentPage from 'src/lib/contexts/PaymentPageContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { MORE_PLANS_COUNT, PRICE_PLACEHOLDER, SAVINGS_PLACEHOLDER } from './subscriptionPreviewData'

const SubscriptionPreviewBlock = ({ onMorePlansClick }: { onMorePlansClick: () => void }) => {
  const { t } = useLocale()
  const {
    getters: { subscriptionConfig },
    actions: { setSelection }
  } = usePaymentPage()

  const { subscriptionCard, summaryTitles } = subscriptionConfig.layoutUi

  const selectLayout = () => setSelection({ type: 'LAYOUT', id: null })

  const planTitle = (intervalCount: number, durationCount: number) => (
    <span className='text-[14px] font-semibold leading-[18px]' style={{ color: titleColor }}>
      <span style={{ opacity: 0.8 }}>
        {t('subscriptions.plan_every')} {intervalCount} {t('subscriptions.interval_month', { count: intervalCount })}
      </span>{' '}
      <span className='font-medium' style={{ opacity: 0.4 }}>
        {t('subscriptions.plan_for')}
      </span>{' '}
      <span style={{ opacity: 0.8 }}>
        {durationCount} {t('subscriptions.interval_month', { count: durationCount })}
      </span>
    </span>
  )

  const cardColors = {
    background: subscriptionCard.backgroundColor || undefined,
    text: subscriptionCard.textColor || undefined,
    ring: subscriptionCard.highlightBackgroundColor || undefined,
    icon: subscriptionCard.iconColor || undefined,
    savings: subscriptionCard.offerColor || undefined,
    stripBackground: subscriptionCard.highlightBackgroundColor || undefined,
    stripText: subscriptionCard.highlightTextColor || undefined
  }

  const titleColor = subscriptionCard.textColor || '#27272A'

  const noteBackground = titleColor.startsWith('#') ? hexToRGBA(titleColor, 0.04) : titleColor

  const scheduleLines = summaryTitles.filter(Boolean).map(line =>
    line
      .replace(/\{frequency\}/g, t('subscriptions.interval_month', { count: 1 }))
      .replace(/\{date_of_order\}/g, '24 May')
      .replace(/\{upcoming_order\}/g, '24 Jun')
      .replace(/\{duration_date\}/g, '24 Aug')
      .replace(/\{delivery_count\}/g, '6')
  )

  return (
    <div className='flex flex-col' onClick={selectLayout}>
      <div className='flex flex-col gap-2 px-4 pt-3'>
        <h2 className='text-text-xs-medium text-coal-light px-4'>{t('payments_tab.choose_plan')}</h2>

        <SubscriptionPlanPreview
          title={planTitle(2, 6)}
          subtitle={t('subscriptions.preview_delivery_count', { count: 6 })}
          price={PRICE_PLACEHOLDER}
          savings={SAVINGS_PLACEHOLDER}
          morePlansLabel={t('subscriptions.preview_more_plans', { count: MORE_PLANS_COUNT })}
          onMorePlansClick={onMorePlansClick}
          colors={cardColors}
        />

        <ul className='flex flex-col gap-0.5 rounded-lg px-2.5 py-3.5' style={{ backgroundColor: noteBackground }}>
          {scheduleLines.map(line => (
            <li key={line} className='flex items-start gap-1.5'>
              <span
                aria-hidden
                className='mt-1.5 h-1 w-1 shrink-0 rounded-full'
                style={{ backgroundColor: titleColor, opacity: 0.4 }}
              />
              <span className='text-text-xxs-regular' style={{ color: titleColor, opacity: 0.6 }}>
                {line}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default SubscriptionPreviewBlock
